import { StepAction } from './stepAction';
import { extractElementId, delay } from '../../utils';
import type { TextStep } from 'clickim/src/common/models/textStep';

export class TextStepAction extends StepAction<TextStep> {
    private async setValueNative() {
        const context = this.context;
        const target = this.getTarget();
        if (!this.step.delayBetweenChars) {
            return this.driver.setValue(target.seleniumElement, context.stepText!);
        }

        await this.driver.elementIdClear(extractElementId(target.seleniumElement));
        return await this.setTextDelayed();
    }

    private async setValueJS() {
        const { step: { events, type: eventType, quirks }, context, sessionPlayerInit } = this;
        const target = this.getTarget();
        const timeout = context.data.timeToPlayStep! + 3000;

        const { setTextDraftJs } = sessionPlayerInit.codeSnippets;
        if (target.isDraftEditor && setTextDraftJs) {
            return this.driver.executeJS(setTextDraftJs(target.locatedElement, context.stepText!));
        }

        if (!events?.length) {
            return undefined;
        }

        const eventMessage = {
            eventType,
            events,
            quirks,
            locatedElement: target.locatedElement,
            isRoot: target.isRoot,
            elementToFocusLocatedElement: target.elementToFocusLocatedElement,
        };

        const [{ setText: setTextScript }, { dispatchFocus }] = await Promise.all([import('./scripts/setText'), import('./scripts/focusElement')]);

        const setTextCode = `
            var getLocatedElement = ${sessionPlayerInit.codeSnippets.getLocatedElementCode};
            var dispatchFocus = ${dispatchFocus};
            var setText = ${setTextScript.toString()};
            var eventData = arguments[0];
            var done = arguments[1];
            return setText.call(null, eventData, done);
        `;

        type CallbackReturnType = Parameters<Parameters<typeof setTextScript>[1]>[0];
        const result = await this.driver.executeCodeAsync<CallbackReturnType, [typeof eventMessage]>(setTextCode, timeout, eventMessage);
        return { success: Boolean(result.value?.success) };
    }

    private async setTextDelayed() {
        const letters = this.context.stepText!;
        const target = this.getTarget();
        for (let i = 0; i < letters.length; i++) {
            await this.driver.elementIdValue(extractElementId(target.seleniumElement), letters[i]);
            // don't delay on last step.
            if (i < letters.length - 1) {
                await delay(this.step.delayBetweenChars || 0);
            }
        }
    }

    private async setValueAppendNative() {
        const keys = [];
        const context = this.context;
        const target = this.getTarget();

        if (target?.seleniumElement) {
            if (!this.step.delayBetweenChars) {
                keys.push(Array.from(context.stepText!));
                // @ts-expect-error this is either a bug, ot a typing issue. but this is TDK only so 💩
                // TODO: (Chemi) Fix later
                return this.driver.elementIdValue(extractElementId(target.seleniumElement), keys);
            }

            return this.setTextDelayed();
        }
        throw new Error('missing selenium element');
    }

    override async performAction() {
        const target = this.getTarget();
        const forceJsEvent = this.driver.isSafari() &&
            target.locatedElement && target.locatedElement.shadowPath &&
            Array.isArray(target.locatedElement.shadowPath) && target.locatedElement.shadowPath.length > 1;

        if (this.step.nativeEvents && !forceJsEvent) {
            return this.setValueNative();
        }
        return this.setValueJS();
    }
}
